import { useState, useEffect } from 'react';
import { Button } from '@/app/components/ui/button';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import type { Category } from '@/services/api';

interface CategoryEditorProps {
  categories: Category[];
  onSave: (categories: Category[]) => void;
}

export function CategoryEditor({ categories, onSave }: CategoryEditorProps) {
  const [localCategories, setLocalCategories] = useState<Category[]>(categories);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    console.log('CategoryEditor received categories:', categories);
    setLocalCategories(categories);
  }, [categories]);

  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setLocalCategories(categories); // Reset to original
  };

  const handleChange = (index: number, field: 'id' | 'nameHu' | 'nameEn', value: string) => {
    const updated = [...localCategories];
    if (field === 'id') {
      updated[index].id = value;
    } else if (field === 'nameHu') {
      updated[index].name.hu = value;
    } else if (field === 'nameEn') {
      updated[index].name.en = value;
    }
    setLocalCategories(updated);
  };

  const handleAdd = () => {
    const newCategory: Category = {
      id: '',
      name: {
        hu: '',
        en: ''
      }
    };
    setLocalCategories([...localCategories, newCategory]);
    setEditingIndex(localCategories.length);
  };

  const handleDelete = (index: number) => {
    if (window.confirm('Biztosan törölni szeretnéd ezt a kategóriát?')) {
      const updated = localCategories.filter((_, i) => i !== index);
      setLocalCategories(updated);
      if (editingIndex === index) {
        setEditingIndex(null);
      }
    }
  };

  const handleSave = () => {
    // Validate
    for (const cat of localCategories) {
      if (!cat.id || !cat.name.hu || !cat.name.en) {
        alert('Minden mezőt ki kell tölteni!');
        return;
      }
    }
    
    onSave(localCategories);
    setEditingIndex(null);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Projekt Kategóriák</h3>
        <Button onClick={handleAdd} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Új kategória
        </Button>
      </div>

      <div className="space-y-4">
        {localCategories.map((category, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            {editingIndex === index ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategória ID (technikai azonosító)
                  </label>
                  <input
                    type="text"
                    value={category.id}
                    onChange={(e) => handleChange(index, 'id', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="pl. residential"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ez az azonosító a projektekhez lesz rendelve. Ne használj szóközöket, használj '_' vagy '-' karaktert.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Név (Magyar)
                    </label>
                    <input
                      type="text"
                      value={category.name.hu}
                      onChange={(e) => handleChange(index, 'nameHu', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="pl. Lakóépület"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name (English)
                    </label>
                    <input
                      type="text"
                      value={category.name.en}
                      onChange={(e) => handleChange(index, 'nameEn', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g. Residential"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                    <X className="h-4 w-4 mr-1" />
                    Mégse
                  </Button>
                  <Button size="sm" onClick={handleSave}>
                    Mentés
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="font-medium">
                  {category.name.hu} / {category.name.en}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-blue-600 hover:text-blue-900 p-2"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-red-600 hover:text-red-900 p-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {localCategories.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            Még nincsenek kategóriák. Kattints az "Új kategória" gombra az első létrehozásához.
          </div>
        )}
      </div>
    </div>
  );
}